import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { onboardingAPI } from "../../services/api.js";
import {
  // Category icons
  Sparkles,      // General
  Terminal,      // Engineering
  TrendingUp,    // Finance
  Kanban,        // Product
  Megaphone,     // Marketing
  Users,         // HR
  Scale,         // Legal
  Rocket,        // Founders
  ChevronRight,

  // General agents
  Newspaper,     // News summariser
  CloudSun,      // Weather report
  Trophy,        // Cricket score
  Camera,        // Instagram poster
  Briefcase,     // LinkedIn poster
  Mail,          // Email drafter
  Globe,         // Web researcher
  ClipboardList, // Meeting noter

  // Engineering agents
  Code2,         // Dev agent
  Bug,           // QA agent
  GitPullRequest,// Code reviewer
  Container,     // DevOps agent
  SearchCode,    // RCA analyst
  FileJson,      // API documenter
  ShieldAlert,   // Security scanner

  // Finance agents
  BarChart3,     // Financial analyst
  Receipt,       // CA assistant
  LineChart,     // Market analyst
  FileText,      // Invoice parser
  Wallet,        // Budget planner
  PieChart,      // Portfolio tracker

  // Product agents
  FileEdit,      // PRD writer
  Map,           // Roadmap planner
  Microscope,    // User research
  GitCommit,     // Changelog writer
  Binoculars,    // Competitor tracker

  // Marketing agents
  PenLine,       // Content writer
  Search,        // SEO agent
  Zap,           // Ad copywriter
  Fingerprint,   // Brand voice checker
  CalendarDays,  // Campaign planner

  // HR agents
  FileBadge,     // JD writer
  GraduationCap, // Interview coach
  BookOpen,      // Onboarding planner
  ScrollText,    // Policy drafter
  Activity,      // Pulse analyser

  // Legal agents
  FileSearch,    // Contract reviewer
  FileLock,      // NDA drafter
  ShieldCheck,   // Compliance checker
  BookMarked,    // Policy writer

  // Founders agents
  Presentation,  // Pitch writer
  Compass,       // Strategy advisor
  Coins,         // Fundraise tracker
  Target,        // OKR planner
  Network,       // Hiring planner
  Bot,           // Agents discovered from a remote domain service
  Check,
} from "lucide-react";

/* ─── Agent catalogue ─────────────────────────────────────────────────────── */
// The id/name pairs here are the canonical agent-domain taxonomy — kept in
// `client/src/config/agentDomains.ts` (AGENT_DOMAINS) for other UI (Agent
// Builder's Domain field, the Agents page filter, New Task's quick-pick
// pills) to import. Not extracted into a shared array here too since this
// literal also carries per-category icons and sample agents that those
// consumers don't need — just keep the id/name values in sync if you add,
// rename, or remove a category.
const CATEGORIES = [
  {
    id: "general", name: "General", Icon: Sparkles, agents: [
      { id: "g1", name: "News summariser",  desc: "Daily briefings from any source",         Icon: Newspaper },
      { id: "g2", name: "Weather report",   desc: "Forecasts and alerts by location",         Icon: CloudSun },
      { id: "g3", name: "Cricket score",    desc: "Live scores, stats, commentary",            Icon: Trophy },
      { id: "g4", name: "Instagram poster", desc: "Caption, hashtag, schedule posts",          Icon: Camera },
      { id: "g5", name: "LinkedIn poster",  desc: "Draft and post professional content",       Icon: Briefcase },
      { id: "g6", name: "Email drafter",    desc: "Context-aware email writing",               Icon: Mail },
      { id: "g7", name: "Web researcher",   desc: "Search, summarise, cite sources",           Icon: Globe },
      { id: "g8", name: "Meeting noter",    desc: "Transcribe calls, extract actions",         Icon: ClipboardList },
    ],
  },
  {
    // Engineering's agents are discovered live from AgenticSDLC (see REMOTE_DOMAIN
    // below), so this list is NOT rendered. It is kept only so previously saved
    // c1–c7 picks still count toward this domain when saving.
    id: "coding", name: "Engineering", Icon: Terminal, agents: [
      { id: "c1", name: "Dev agent",        desc: "Write, refactor, explain code",             Icon: Code2 },
      { id: "c2", name: "QA agent",         desc: "Test cases, bug reports, coverage",         Icon: Bug },
      { id: "c3", name: "Code reviewer",    desc: "Review PRs, enforce standards",             Icon: GitPullRequest },
      { id: "c4", name: "DevOps agent",     desc: "CI/CD, infra, deployment scripts",          Icon: Container },
      { id: "c5", name: "RCA analyst",      desc: "Root cause analysis from logs",             Icon: SearchCode },
      { id: "c6", name: "API documenter",   desc: "Auto-generate API docs and specs",          Icon: FileJson },
      { id: "c7", name: "Security scanner", desc: "Find vulnerabilities, suggest fixes",       Icon: ShieldAlert },
    ],
  },
  {
    id: "finance", name: "Finance", Icon: TrendingUp, agents: [
      { id: "f1", name: "Financial analyst", desc: "Ratios, forecasts, variance",              Icon: BarChart3 },
      { id: "f2", name: "CA assistant",      desc: "Tax, compliance, audit prep",              Icon: Receipt },
      { id: "f3", name: "Market analyst",    desc: "Sector trends, competitor intel",          Icon: LineChart },
      { id: "f4", name: "Invoice parser",    desc: "Extract line items from PDFs",             Icon: FileText },
      { id: "f5", name: "Budget planner",    desc: "Build and track budgets",                  Icon: Wallet },
      { id: "f6", name: "Portfolio tracker", desc: "Stock and MF performance digest",          Icon: PieChart },
    ],
  },
  {
    id: "product", name: "Product", Icon: Kanban, agents: [
      { id: "p1", name: "PRD writer",         desc: "Product specs, user stories",             Icon: FileEdit },
      { id: "p2", name: "Roadmap planner",    desc: "Prioritise features, set milestones",     Icon: Map },
      { id: "p3", name: "User research",      desc: "Synthesise interviews, surface themes",   Icon: Microscope },
      { id: "p4", name: "Changelog writer",   desc: "Release notes from commit logs",          Icon: GitCommit },
      { id: "p5", name: "Competitor tracker", desc: "Monitor competitor moves weekly",         Icon: Binoculars },
    ],
  },
  {
    id: "marketing", name: "Marketing", Icon: Megaphone, agents: [
      { id: "m1", name: "Content writer",      desc: "Blog posts, newsletters, scripts",       Icon: PenLine },
      { id: "m2", name: "SEO agent",           desc: "Keywords, meta, on-page audits",         Icon: Search },
      { id: "m3", name: "Ad copywriter",       desc: "Google, Meta, LinkedIn ad copy",         Icon: Zap },
      { id: "m4", name: "Brand voice checker", desc: "Ensure tone consistency",                Icon: Fingerprint },
      { id: "m5", name: "Campaign planner",    desc: "Calendar, briefs, go-to-market",         Icon: CalendarDays },
    ],
  },
  {
    id: "hr", name: "HR", Icon: Users, agents: [
      { id: "h1", name: "JD writer",          desc: "Job descriptions by role and level",      Icon: FileBadge },
      { id: "h2", name: "Interview coach",    desc: "Question banks, scoring rubrics",          Icon: GraduationCap },
      { id: "h3", name: "Onboarding planner", desc: "30-60-90 day plans",                      Icon: BookOpen },
      { id: "h4", name: "Policy drafter",     desc: "HR policies and handbooks",               Icon: ScrollText },
      { id: "h5", name: "Pulse analyser",     desc: "Summarise survey feedback",               Icon: Activity },
    ],
  },
  {
    id: "legal", name: "Legal", Icon: Scale, agents: [
      { id: "l1", name: "Contract reviewer",  desc: "Flag clauses, summarise risks",           Icon: FileSearch },
      { id: "l2", name: "NDA drafter",        desc: "Draft and redline NDAs",                  Icon: FileLock },
      { id: "l3", name: "Compliance checker", desc: "GDPR, DPDP, SOC2 readiness",             Icon: ShieldCheck },
      { id: "l4", name: "Policy writer",      desc: "Compliant internal policies",             Icon: BookMarked },
    ],
  },
  {
    id: "founders", name: "Founders", Icon: Rocket, agents: [
      { id: "fo1", name: "Pitch writer",      desc: "Decks, one-pagers, investor emails",      Icon: Presentation },
      { id: "fo2", name: "Strategy advisor",  desc: "Market sizing, positioning, moats",       Icon: Compass },
      { id: "fo3", name: "Fundraise tracker", desc: "VC outreach, round status",               Icon: Coins },
      { id: "fo4", name: "OKR planner",       desc: "Set and track company OKRs",              Icon: Target },
      { id: "fo5", name: "Hiring planner",    desc: "Org design, role sequencing",             Icon: Network },
    ],
  },
];

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const css = `
  .ob-overlay{
    position:fixed;inset:0;background:rgba(0,0,0,0.45);
    display:flex;align-items:center;justify-content:center;
    z-index:1000;padding:16px;
  }
  .ob-wrap{
    background:var(--color-background-tertiary,var(--app-surface-3));
    padding:20px;border-radius:16px;width:100%;max-width:660px;
  }
  .ob-modal{
    background:var(--color-background-primary,var(--lyra-color-surface));
    border-radius:20px;border:0.5px solid var(--color-border-tertiary,var(--lyra-color-border));
    overflow:hidden;
  }
  .ob-topbar{
    display:flex;align-items:center;justify-content:space-between;
    padding:18px 22px 0;
  }
  .ob-brand{
    display:flex;align-items:center;gap:7px;
    font-size:12px;font-weight:500;
    color:var(--color-text-secondary,var(--lyra-color-text-muted));
  }
  .ob-dot{
    width:7px;height:7px;border-radius:50%;background:var(--lyra-color-primary);
  }
  .ob-skip{
    font-size:12px;color:var(--color-text-tertiary,var(--app-text-3));
    cursor:pointer;background:none;border:none;padding:0;
  }
  .ob-skip:hover{color:var(--color-text-secondary,var(--lyra-color-text-muted));}

  .ob-hero{padding:28px 22px 0;}
  .ob-eyebrow{
    font-size:11px;font-weight:500;color:var(--lyra-color-primary);
    letter-spacing:.06em;text-transform:uppercase;margin-bottom:8px;
  }
  .ob-title{
    font-size:22px;font-weight:500;
    color:var(--color-text-primary,var(--lyra-color-text));
    line-height:1.25;margin-bottom:6px;
  }
  .ob-sub{
    font-size:13px;color:var(--color-text-secondary,var(--lyra-color-text-muted));line-height:1.5;
  }
  .ob-domain-picker{padding:22px 22px 0;}
  .ob-domain-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}
  .ob-domain-card{
    display:flex;align-items:center;gap:10px;text-align:left;width:100%;cursor:pointer;
    padding:11px;border-radius:11px;border:1px solid var(--color-border-tertiary,var(--lyra-color-border));
    background:var(--color-background-primary,var(--lyra-color-surface));transition:all .12s;
  }
  .ob-domain-card:hover{background:var(--color-background-secondary,var(--lyra-color-surface-2));border-color:var(--color-border-secondary,var(--app-border-strong));}
  .ob-domain-card.selected{border:1.5px solid var(--lyra-color-primary);background:var(--lyra-color-surface-2);}
  .ob-domain-card.selected .ob-card-icon{background:var(--app-primary-soft);color:var(--lyra-color-primary);}
  .ob-domain-copy{flex:1;min-width:0;}
  .ob-domain-note{font-size:11.5px;color:var(--color-text-tertiary,var(--app-text-3));line-height:1.45;margin-top:12px;}

  .ob-layout{
    display:grid;grid-template-columns:1fr 1fr;
    gap:0;margin-top:22px;
  }
  .ob-left{
    padding:0 0 0 22px;
    border-right:0.5px solid var(--color-border-tertiary,var(--lyra-color-border));
  }
  .ob-col-label{
    font-size:10px;font-weight:500;
    color:var(--color-text-tertiary,var(--app-text-3));
    letter-spacing:.06em;text-transform:uppercase;
    padding:0 0 10px 0;margin-top:4px;
  }
  .ob-cat-list{display:flex;flex-direction:column;gap:2px;}
  .ob-cat-btn{
    display:flex;align-items:center;justify-content:space-between;
    padding:9px 12px 9px 0;border:none;background:none;cursor:pointer;
    border-radius:8px;text-align:left;width:100%;transition:background .12s;
  }
  .ob-cat-btn:hover{background:var(--color-background-secondary,var(--lyra-color-surface-2));}
  .ob-cat-btn.active{background:var(--lyra-color-surface-2);}
  .ob-cat-left{display:flex;align-items:center;gap:8px;}
  .ob-cat-icon{
    width:28px;height:28px;border-radius:8px;
    display:flex;align-items:center;justify-content:center;
    flex-shrink:0;
    background:var(--color-background-secondary,var(--app-surface-3));
    color:var(--color-text-secondary,var(--lyra-color-text-muted));
  }
  .ob-cat-btn.active .ob-cat-icon{background:var(--app-primary-soft);color:var(--lyra-color-primary);}
  .ob-cat-name{
    font-size:13px;font-weight:500;
    color:var(--color-text-primary,var(--lyra-color-text));
  }
  .ob-cat-count{font-size:11px;color:var(--color-text-tertiary,var(--app-text-3));}
  .ob-cat-btn.active .ob-cat-count{color:var(--lyra-color-primary);}
  .ob-chevron{
    color:var(--color-text-tertiary,var(--app-text-3));
    transition:transform .15s;flex-shrink:0;
  }
  .ob-cat-btn.active .ob-chevron{color:var(--lyra-color-primary);}

  .ob-right{padding:0 22px 0 20px;}
  .ob-agents-header{
    display:flex;align-items:center;justify-content:space-between;
    padding-bottom:10px;
  }
  .ob-agents-title{
    font-size:10px;font-weight:500;
    color:var(--color-text-tertiary,var(--app-text-3));
    letter-spacing:.06em;text-transform:uppercase;
  }
  .ob-select-all{
    font-size:11px;color:var(--lyra-color-primary);
    background:none;border:none;cursor:pointer;padding:0;
  }
  .ob-select-all:disabled{opacity:.4;cursor:default;}
  .ob-agent-meta{
    font-size:10px;color:var(--color-text-tertiary,var(--app-text-3));
    margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  }
  .ob-status{
    padding:18px 14px;border:0.5px dashed var(--color-border-secondary,var(--app-border-strong));
    border-radius:12px;font-size:12px;line-height:1.5;
    color:var(--color-text-secondary,var(--lyra-color-text-muted));
  }
  .ob-status strong{display:block;color:var(--color-text-primary,var(--lyra-color-text));font-weight:500;margin-bottom:2px;}
  .ob-status button{margin-top:8px;}
  .ob-cards{
    display:flex;flex-direction:column;gap:6px;
    max-height:310px;overflow-y:auto;padding-right:2px;
  }
  .ob-cards::-webkit-scrollbar{width:3px;}
  .ob-cards::-webkit-scrollbar-track{background:transparent;}
  .ob-cards::-webkit-scrollbar-thumb{
    background:var(--color-border-secondary,var(--app-border-strong));border-radius:2px;
  }
  .ob-card{
    display:flex;align-items:flex-start;gap:10px;
    padding:10px 12px;
    border:0.5px solid var(--color-border-tertiary,var(--lyra-color-border));
    border-radius:12px;cursor:pointer;transition:all .12s;
    background:var(--color-background-primary,var(--lyra-color-surface));
  }
  .ob-card:hover{
    border-color:var(--color-border-secondary,var(--app-border-strong));
    background:var(--color-background-secondary,var(--lyra-color-surface-2));
  }
  .ob-card.picked{border:1.5px solid var(--lyra-color-primary);background:var(--lyra-color-surface-2);}
  .ob-card-icon{
    width:32px;height:32px;border-radius:8px;
    background:var(--color-background-secondary,var(--app-surface-3));
    display:flex;align-items:center;justify-content:center;
    color:var(--color-text-secondary,var(--lyra-color-text-muted));flex-shrink:0;
  }
  .ob-card.picked .ob-card-icon{background:var(--app-primary-soft);color:var(--lyra-color-primary);}
  .ob-agent-info{flex:1;min-width:0;}
  .ob-agent-name{
    font-size:12px;font-weight:500;
    color:var(--color-text-primary,var(--lyra-color-text));margin-bottom:2px;
  }
  .ob-agent-desc{
    font-size:11px;color:var(--color-text-secondary,var(--lyra-color-text-muted));
    line-height:1.4;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  }
  .ob-check{
    width:16px;height:16px;border-radius:50%;
    border:1.5px solid var(--color-border-secondary,var(--app-border-strong));
    flex-shrink:0;margin-top:2px;
    display:flex;align-items:center;justify-content:center;
  }
  .ob-card.picked .ob-check{
    background:var(--lyra-color-primary);border-color:var(--lyra-color-primary);
  }

  .ob-footer{
    display:flex;align-items:center;justify-content:space-between;
    padding:14px 22px;
    border-top:0.5px solid var(--color-border-tertiary,var(--lyra-color-border));
    margin-top:18px;
  }
  .ob-summary{font-size:12px;color:var(--color-text-secondary,var(--lyra-color-text-muted));}
  .ob-summary strong{
    color:var(--color-text-primary,var(--lyra-color-text));font-weight:500;
  }
  .ob-go{
    background:var(--lyra-color-primary);color:var(--lyra-color-surface);border:none;
    border-radius:8px;padding:9px 18px;
    font-size:13px;font-weight:500;cursor:pointer;
    display:flex;align-items:center;gap:6px;transition:background .12s;
  }
  .ob-go:hover{background:#154d39;}
  .ob-go:disabled{background:var(--color-border-secondary,var(--app-border-strong));cursor:not-allowed;}

  @media (max-width: 1024px){
    .ob-wrap{max-width:760px;}
    .ob-layout{grid-template-columns:1fr;}
    .ob-left{
      border-right:none;
      border-bottom:0.5px solid var(--color-border-tertiary,var(--lyra-color-border));
      padding:0 18px 12px 18px;
    }
    .ob-right{padding:12px 18px 0 18px;}
  }

  @media (max-width: 768px){
    .ob-overlay{
      align-items:flex-end;
      padding:0;
      background:rgba(0,0,0,0.5);
    }
    .ob-wrap{
      max-width:none;
      width:100%;
      height:100%;
      padding:0;
      display:flex;
      align-items:flex-end;
    }
    .ob-modal{
      width:100%;
      max-height:100dvh;
      border-radius:18px 18px 0 0;
      display:flex;
      flex-direction:column;
    }
    .ob-topbar{padding:14px 14px 0;}
    .ob-hero{padding:18px 14px 0;}
    .ob-title{font-size:19px;}
    .ob-sub{font-size:12px;}
    .ob-domain-picker{padding:16px 14px 0;overflow:auto;}
    .ob-domain-grid{grid-template-columns:1fr;}

    .ob-layout{
      display:flex;
      flex-direction:column;
      margin-top:14px;
      min-height:0;
      flex:1;
      overflow:hidden;
    }

    .ob-left{
      padding:0 12px 10px 12px;
      border-right:none;
      border-bottom:0.5px solid var(--color-border-tertiary,var(--lyra-color-border));
    }
    .ob-col-label{padding-bottom:8px;}
    .ob-cat-list{
      flex-direction:row;
      gap:8px;
      overflow-x:auto;
      padding-bottom:4px;
      -webkit-overflow-scrolling:touch;
    }
    .ob-cat-btn{
      min-width:170px;
      padding:8px 10px;
      border:0.5px solid var(--color-border-tertiary,var(--lyra-color-border));
      background:var(--color-background-primary,var(--lyra-color-surface));
      border-radius:10px;
    }
    .ob-cat-btn.active{border-color:var(--lyra-color-primary);}

    .ob-right{
      padding:10px 12px 0;
      min-height:0;
      display:flex;
      flex-direction:column;
      flex:1;
    }
    .ob-agents-header{padding-bottom:8px;}
    .ob-cards{
      flex:1;
      min-height:220px;
      max-height:none;
      padding-right:0;
      padding-bottom:8px;
    }
    .ob-card{padding:9px 10px;}

    .ob-footer{
      margin-top:10px;
      padding:12px;
      flex-direction:column;
      align-items:stretch;
      gap:10px;
    }
    .ob-summary{
      text-align:center;
      font-size:11.5px;
    }
    .ob-go{
      width:100%;
      justify-content:center;
      padding:10px 16px;
    }
  }
`;

/* ─── Remote (service-backed) domains ─────────────────────────────────────── */
// Engineering's agents come from the AgenticSDLC service, via Lyra's backend
// (GET /api/domain-agents/coding). Selections are stored under the namespaced
// `selectionId` the backend returns, e.g. "agenticsdlc:development".
const REMOTE_DOMAIN = "coding";
const REMOTE_ID_PREFIX = "agenticsdlc:";

const remoteAgentMeta = (a) =>
  [...a.capabilities, a.approval?.required ? "approval required" : null].filter(Boolean).join(" · ");

/* ─── Component ───────────────────────────────────────────────────────────── */
// Small, recognizable quick-pick — not the full AiConsole provider catalog,
// which is too heavy to pull into onboarding. Ids must match AI_MODELS keys
// on the server (config/aiModels.js) so verify-key/save-key both resolve.
const ONBOARDING_KEY_PROVIDERS = [
  { id: "openai",        label: "OpenAI",           placeholder: "sk-…" },
  { id: "gemini",        label: "Google Gemini",    placeholder: "AIza…" },
  { id: "claude-sonnet", label: "Anthropic Claude", placeholder: "sk-ant-…" },
  { id: "groq",          label: "Groq",             placeholder: "gsk_…" },
];

export default function OnboardingModal({ onComplete, onSkip, mode = 'onboarding' }) {
  const [selectedDomains, setSelectedDomains] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── BYOK step — shown after agent selection, before finishing onboarding
  // (skippable; the Home page nudges again later if skipped). Not shown in
  // 'preferences' mode (editing settings later), only on first-run onboarding.
  const [step, setStep] = useState("agents"); // "agents" | "apiKey"
  const [keyProvider, setKeyProvider] = useState(ONBOARDING_KEY_PROVIDERS[0].id);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [keyTesting, setKeyTesting] = useState(false);
  const [keySaving, setKeySaving] = useState(false);
  const [keyTestResult, setKeyTestResult] = useState(null);

  /* Load previously saved selections on mount */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { onboarding } = await onboardingAPI.get();
        if (!cancelled) {
          const savedDomains = Array.isArray(onboarding?.domains) ? onboarding.domains : [];
          // Older profiles only have individual agent ids. Infer their domains once so
          // the category-only experience preserves the user's earlier choices.
          const inferred = CATEGORIES.filter((category) => category.agents.some((agent) => (onboarding?.selectedAgents || []).includes(agent.id))).map((category) => category.id);
          setSelectedDomains(new Set(savedDomains.length ? savedDomains : inferred));
        }
      } catch (e) {
        // silently ignore — start with empty selections
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* helpers */
  const n = selectedDomains.size;
  const toggleDomain = (id) => {
    setSelectedDomains(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleLaunch = async () => {
    setSaving(true);
    try {
      const domains = [...selectedDomains];

      const result = await onboardingAPI.save({
        role: "other",
        useCase: "automate",
        experience: "beginner",
        tools: [],
        enabledFeatures: domains,
        domains,
        // The server expands the selected categories to Lyra's managed agent set.
        selectedAgents: [],
      });

      const createdAgents = result?.createdAgents || [];
      if (createdAgents.length > 0) {
        toast.success(
          createdAgents.length === 1
            ? `"${createdAgents[0]}" added to your Agents list`
            : `${createdAgents.length} agents added to your Agents list`
        );
      }

      // Editing settings later (mode='preferences') finishes immediately as
      // before; first-run onboarding goes on to the BYOK step next.
      if (mode === "preferences") {
        if (typeof onComplete === "function") onComplete();
      } else {
        setStep("apiKey");
      }
    } catch (e) {
      console.error("Onboarding save failed:", e);
      if (typeof onComplete === "function") onComplete();
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (typeof onSkip === "function") onSkip();
  };

  const handleTestOnboardingKey = async () => {
    if (!apiKeyInput.trim()) return;
    setKeyTesting(true);
    setKeyTestResult(null);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/usage/test-key`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ modelId: keyProvider, apiKey: apiKeyInput.trim() }),
      });
      const data = await res.json();
      setKeyTestResult(data);
      if (data.ok) toast.success(`Connected · ${data.latencyMs}ms`);
      else toast.error(data.error || "Key test failed");
    } catch (e) {
      setKeyTestResult({ ok: false, error: e.message });
    } finally {
      setKeyTesting(false);
    }
  };

  const handleSaveKeyAndFinish = async () => {
    if (!apiKeyInput.trim()) return;
    setKeySaving(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/agents/custom-api-keys`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ keys: { [keyProvider]: apiKeyInput.trim() } }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data?.details?.[keyProvider] || data?.error || "Failed to save key — check it and try again.";
        toast.error(detail);
        return;
      }
      toast.success("API key connected!");
      if (typeof onComplete === "function") onComplete();
    } catch (e) {
      toast.error(e.message || "Failed to save key");
    } finally {
      setKeySaving(false);
    }
  };

  const handleSkipKeyStep = () => {
    if (typeof onComplete === "function") onComplete();
  };

  return (
    <>
      <style>{css}</style>
      <div className="ob-overlay">
        <div className="ob-wrap">
          <div className="ob-modal">

            {/* Top bar */}
            <div className="ob-topbar">
              <div className="ob-brand">
                <span className="ob-dot" />
                Agent Lyra
              </div>
              <button className="ob-skip" onClick={step === 'apiKey' ? handleSkipKeyStep : handleSkip}>
                {mode === 'preferences' ? 'Done' : 'Skip for now'}
              </button>
            </div>

            {step === 'apiKey' ? (
              <>
                {/* Hero */}
                <div className="ob-hero">
                  <div className="ob-eyebrow">Setup · Step 2 of 2</div>
                  <div className="ob-title">Connect an API key</div>
                  <div className="ob-sub">
                    Your agents need a model to run on. Add your own key now (BYOK — free, use your own quota),
                    or skip for now — you can add one anytime from the Models page, and we'll remind you.
                  </div>
                </div>

                {/* API key form */}
                <div style={{ padding: '4px 32px 8px' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {ONBOARDING_KEY_PROVIDERS.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => { setKeyProvider(p.id); setKeyTestResult(null); }}
                        style={{
                          padding: '8px 14px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                          border: `1.5px solid ${keyProvider === p.id ? 'var(--app-primary-mid)' : 'var(--lyra-color-border)'}`,
                          background: keyProvider === p.id ? 'var(--app-primary-soft)' : 'var(--lyra-color-surface)',
                          color: keyProvider === p.id ? 'var(--app-primary-deep)' : 'var(--app-text-2)',
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="password"
                      value={apiKeyInput}
                      onChange={(e) => { setApiKeyInput(e.target.value); setKeyTestResult(null); }}
                      placeholder={ONBOARDING_KEY_PROVIDERS.find((p) => p.id === keyProvider)?.placeholder || 'API key…'}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 9, fontSize: 13, outline: 'none', fontFamily: 'inherit',
                        border: `1.5px solid ${keyTestResult?.ok ? 'var(--app-success-border)' : keyTestResult?.ok === false ? 'var(--app-danger-border)' : 'var(--lyra-color-border)'}`,
                      }}
                    />
                    <button
                      onClick={handleTestOnboardingKey}
                      disabled={!apiKeyInput.trim() || keyTesting}
                      style={{
                        padding: '10px 16px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: apiKeyInput.trim() ? 'pointer' : 'not-allowed',
                        border: '1.5px solid var(--lyra-color-border)', background: 'var(--lyra-color-surface)', color: 'var(--app-text-2)', opacity: apiKeyInput.trim() ? 1 : 0.5, flexShrink: 0,
                      }}
                    >
                      {keyTesting ? 'Testing…' : 'Test'}
                    </button>
                  </div>
                  {keyTestResult && !keyTestResult.ok && (
                    <div style={{ fontSize: 12, color: 'var(--lyra-color-danger)', marginTop: 6, fontWeight: 500 }}>✗ {keyTestResult.error}</div>
                  )}
                  {keyTestResult?.ok && (
                    <div style={{ fontSize: 12, color: 'var(--lyra-color-success)', marginTop: 6, fontWeight: 600 }}>✓ Connected · {keyTestResult.latencyMs}ms</div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Hero */}
                <div className="ob-hero">
                  <div className="ob-eyebrow">{mode === 'preferences' ? 'Preferences' : 'Setup · Step 1 of 2'}</div>
                  <div className="ob-title">Which areas should Lyra support?</div>
                  <div className="ob-sub">
                    Choose the domains for your workspace. Lyra automatically provides the right capabilities for each one.
                  </div>
                </div>

                <div className="ob-domain-picker">
                  <div className="ob-col-label">Workspace domains</div>
                  <div className="ob-domain-grid">
                    {CATEGORIES.map((category) => {
                      const Icon = category.Icon;
                      const selected = selectedDomains.has(category.id);
                      return <button key={category.id} type="button" className={`ob-domain-card${selected ? ' selected' : ''}`} onClick={() => toggleDomain(category.id)}>
                        <div className="ob-card-icon"><Icon size={17} strokeWidth={1.8} /></div>
                        <div className="ob-domain-copy"><div className="ob-agent-name">{category.name}</div><div className="ob-agent-desc">Lyra will provide relevant capabilities</div></div>
                        <div className="ob-check">{selected && <Check size={9} strokeWidth={3} color="var(--app-on-accent)" />}</div>
                      </button>;
                    })}
                  </div>
                  <div className="ob-domain-note">You can change domains later. Individual Lyra agents are managed and refreshed automatically.</div>
                </div>
              </>
            )}

            {/* Footer */}
            <div className="ob-footer">
              {step === 'apiKey' ? (
                <>
                  <div className="ob-summary">
                    {keyTestResult?.ok
                      ? <><strong>Key verified</strong> — ready to connect</>
                      : apiKeyInput.trim()
                      ? 'Test it, or just connect — we verify before saving'
                      : "No key yet? That's fine — skip for now"}
                  </div>
                  <button
                    className="ob-go"
                    disabled={!apiKeyInput.trim() || keySaving}
                    onClick={handleSaveKeyAndFinish}
                  >
                    {keySaving ? "Saving…" : "Connect & finish"}
                    {!keySaving && <ChevronRight size={14} strokeWidth={2.2} />}
                  </button>
                </>
              ) : (
                <>
                  <div className="ob-summary">
                    {loading ? (
                      <span style={{ color: 'var(--color-text-tertiary,var(--app-text-3))' }}>Loading your selections…</span>
                    ) : n > 0 ? (
                      <><strong>{n} domain{n > 1 ? "s" : ""}</strong> enabled for your workspace</>
                    ) : (
                      "Choose at least one domain"
                    )}
                  </div>
                  <button
                    className="ob-go"
                    disabled={n === 0 || saving || loading}
                    onClick={handleLaunch}
                  >
                    {saving ? "Saving…" : mode === 'preferences' ? 'Save changes' : 'Continue'}
                    {!saving && <ChevronRight size={14} strokeWidth={2.2} />}
                  </button>
                </>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
